import React, { useState, useRef } from 'react';
import './PopUpModal.css';
import { FaAngleDown } from 'react-icons/fa';
import { saveEnquiry } from '../functions/Enquiry';

const PopUpModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    tripType: '',
    travelDate: '',
    travellerCount: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formRef = useRef(null); // Add ref for hidden form

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({...errors, [name]: null});
    }
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.fullName) tempErrors.fullName = "Full name is required";
    if (!formData.email) tempErrors.email = "Email is required";
    if (!formData.phone) tempErrors.phone = "Phone number is required";
    if (!formData.tripType) tempErrors.tripType = "Category is Required";
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);

      // Prepare data in the structure expected by saveEnquiry
      const enquiryData = {
        contactInfo: {
          name: formData.fullName.trim(),
          email: formData.email,
          phone: formData.phone,
        },
        // These need to be nested within 'details' to match the expected structure in saveEnquiry
        details: {
          tripType: formData.tripType,
          travelDate: formData.travelDate,
          travellerCount: formData.travellerCount,
          message: formData.message
        }
      };

      try {
        // Save to Firestore
        await saveEnquiry(enquiryData);

        // Submit to formsubmit.co via hidden form
        if (formRef.current) {
          formRef.current.submit();
        }

        setIsSubmitted(true);
        setIsSubmitting(false);

        // Reset form after successful submission
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          tripType: '',
          travelDate: '',
          travellerCount: '',
          message: ''
        });

        // Close modal after delay
        setTimeout(() => {
          setIsSubmitted(false);
          onClose();
        }, 3000);
      } catch (error) {
        // console.error('Error submitting form:', error);
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        {/* Hidden form for formsubmit.co */}
        <form
          ref={formRef}
          action="https://formsubmit.co/swayourwayleads@gmail.com"
          method="POST"
          target="hidden-formsubmit-frame"
          style={{ display: 'none' }}
        >
          <input type="hidden" name="_template" value="table" />
          <input type="hidden" name="Name" value={formData.fullName.trim()} />
          <input type="hidden" name="Email" value={formData.email} />
          <input type="hidden" name="Phone" value={formData.phone} />
          <input type="hidden" name="Trip Type" value={formData.tripType} />
          <input type="hidden" name="Travel Date" value={formData.travelDate} />
          <input type="hidden" name="Traveller Count" value={formData.travellerCount} />
          <input type="hidden" name="Message" value={formData.message} />
          <input type="hidden" name="_captcha" value="false" />
        </form>
        {/* Hidden iframe for formsubmit.co */}
        <iframe
          name="hidden-formsubmit-frame"
          style={{ display: 'none' }}
          title="hidden-formsubmit-frame"
        />
        
        {isSubmitted ? (
          <div className="success-message">
            <h2>Thank You!</h2>
            <p>We'll contact you shortly with details about your trip!</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Plan Your Next Trip</h2>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={errors.fullName ? "error" : ""}
                      placeholder="Full Name *"
                    />
                    {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? "error" : ""}
                      placeholder="Email *"
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group phone-group">
                    <div className="country-code">
                      <span className="country-flag">🇮🇳</span>
                      <span className="code">+91</span>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={errors.phone ? "error phone-input" : "phone-input"}
                      placeholder="Phone Number *"
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>
                  
                  <div className="form-group">
                    <input
                      type="date"
                      id="travelDate"
                      name="travelDate"
                      value={formData.travelDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      placeholder="Travel Date"
                    />
                  </div>
                  
                  <div className="form-group">
                    <input
                      type="number"
                      id="travellerCount"
                      name="travellerCount"
                      value={formData.travellerCount}
                      onChange={handleChange}
                      min="1"
                      placeholder="Number of Travellers"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="tripType" className="question-label">What kind of trip do you prefer? *</label>
                  <div className="select-wrapper">
                    <select
                      id="tripType"
                      name="tripType"
                      value={formData.tripType}
                      onChange={handleChange}
                      className={errors.tripType ? "error" : ""}
                    >
                      <option value="">Select a category</option>
                      <option value="backpacking">Backpacking Trips</option>
                      <option value="biking">Biking Trips</option>
                      <option value="treks">Treks</option>
                      <option value="corporate">Corporate Tour</option>
                      <option value="weekend">Weekend Getaways</option>
                      <option value="honeymoon">Honeymoon Tour</option>
                      <option value="domestic">Domestic Packages</option>
                      <option value="international">International Tour</option>
                    </select>
                    <FaAngleDown className="select-arrow" />
                  </div>
                  {errors.tripType && <span className="error-message">{errors.tripType}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="question-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Please share any special requirements or questions you have about your trip"
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : "Send Enquiry"}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PopUpModal;
